import type { ExerciseView, LessonContentBlock, LessonDetail } from '@shared/types/catalog'

/**
 * A lesson is played as a flat list of steps: teaching material first, then
 * each activity's exercises in order.
 *
 * Flattening here rather than in the player keeps navigation trivial (an index
 * into an array) and makes "resume where you left off" a single integer, which
 * is exactly what `lesson_progress.last_position` stores.
 */

export type LessonStep =
  | { kind: 'block'; key: string; block: LessonContentBlock }
  | {
      kind: 'exercise'
      key: string
      exercise: ExerciseView
      activityTitle: string | null
      instructions: string | null
    }

export function buildSteps(lesson: LessonDetail): LessonStep[] {
  const steps: LessonStep[] = lesson.blocks.map((block) => ({
    kind: 'block',
    key: `block:${block.id}`,
    block,
  }))

  for (const activity of lesson.activities) {
    for (const exercise of activity.exercises) {
      steps.push({
        kind: 'exercise',
        key: `exercise:${exercise.id}`,
        exercise,
        activityTitle: activity.title,
        instructions: activity.instructions,
      })
    }
  }

  return steps
}

export function countExercises(steps: LessonStep[]): number {
  return steps.filter((s) => s.kind === 'exercise').length
}
